import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { authApi } from "../api/auth";
import { Input } from "../components/ui/Input";
import { Button } from "../components/ui/Button";

const schema = z
  .object({
    // Accepts either a 64-char hex link token or a 6-digit admin code
    token: z.string().min(1, "Token or code is required"),
    newPassword: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlToken = searchParams.get("token") ?? "";

  const [tokenState, setTokenState] = useState<"checking" | "valid" | "invalid">(
    urlToken ? "checking" : "valid"
  );
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { token: urlToken },
  });

  // If a token came in via URL, pre-validate it
  useEffect(() => {
    if (!urlToken) return;
    authApi
      .verifyToken(urlToken, "reset")
      .then((res) => setTokenState(res.valid ? "valid" : "invalid"))
      .catch(() => setTokenState("invalid"));
  }, [urlToken]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await authApi.resetPassword(data.token.trim(), data.newPassword);
      setDone(true);
      toast.success("Password reset! You can now log in.");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to reset password. The code or link may have expired."
      );
    } finally {
      setLoading(false);
    }
  };

  if (tokenState === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-8 h-8 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (tokenState === "invalid") {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-stone-50 px-4">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-stone-200 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-display font-bold text-stone-900 mb-2">Link expired</h2>
          <p className="text-stone-500 text-sm mb-6">
            This reset link is invalid or has expired. Please request a new one.
          </p>
          <Link to="/forgot-password" className="text-sm text-forest-600 hover:underline font-medium">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-stone-50 px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
        <img src="/ch-logo-green.png" alt="Camp Highlands" className="h-12 w-auto mb-8 mx-auto" />

        {done ? (
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-forest-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-forest-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-display font-bold text-stone-900 mb-2">Password reset!</h2>
            <p className="text-stone-500 text-sm">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-display font-bold text-stone-900 mb-1">Set new password</h1>
            <p className="text-stone-500 text-sm mb-6">
              {urlToken
                ? "Choose a new password for your account."
                : "Enter the 6-digit code from your email and choose a new password."}
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              {!urlToken && (
                <Input
                  label="Reset code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  hint="Enter the 6-digit code from your email"
                  error={errors.token?.message}
                  {...register("token")}
                  onChange={(e) => setValue("token", e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              )}

              <Input
                label="New password"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                error={errors.newPassword?.message}
                {...register("newPassword")}
              />

              <Input
                label="Confirm password"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                error={errors.confirmPassword?.message}
                {...register("confirmPassword")}
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                className="w-full mt-2"
              >
                Reset password
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-sm text-stone-500 hover:text-stone-700">
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
